import { AnalyticsPage, Channel } from '@/types/analytics';
import { formatNumber } from '@/util/number';
import ListAltIcon from '@mui/icons-material/ListAlt';
import StorageIcon from '@mui/icons-material/Storage';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import WavesIcon from '@mui/icons-material/Waves';
import { Box, IconButton, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';

export const databaseColumns = (): GridColDef[] => {
  return [
    {
      field: 'timestamp',
      headerName: 'Timestamp',
      valueGetter: (value) => dayjs(value).format('YYYY-MM-DD HH:mm:ss:SSS'),
      sortComparator: (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime(),
      flex: 1
    },
    {
      field: 'queryid',
      headerName: 'Query Id',
      sortComparator: (v1, v2) => v1.localeCompare(v2, undefined, { numeric: true })
    },
    {
      field: 'step',
      headerName: 'Step',
      sortComparator: (v1, v2) => v1.localeCompare(v2, undefined, { numeric: true }),
      align: 'right'
    },
    { field: 'blockid', headerName: 'Block Id', sortComparator: (v1, v2) => Number(v1) - Number(v2), align: 'right' },
    { field: 'blockname', headerName: 'Block Name', flex: 2 },
    {
      field: 'performance',
      headerName: 'Exec time (ms)',
      valueGetter: (value: string) => Number(value.replace('Execution time: ', '').replace(' ms', '')),
      valueFormatter: (value: number) => formatNumber(value),
      sortComparator: (v1, v2) => Number(v1) - Number(v2),
      align: 'right'
    },
    { field: 'query', headerName: 'Query', flex: 2 },
    {
      field: 'returnValue',
      headerName: 'Return',
      valueFormatter: (value: string) => (value ? formatNumber(Number(value.replace('Return value: ', ''))) : ''),
      align: 'right'
    },
    { field: 'response', headerName: 'Response', flex: 2 }
  ];
};

export const appColumns = (): GridColDef[] => {
  return [
    {
      field: 'timestamp',
      headerName: 'Timestamp',
      valueGetter: (value) => dayjs(value).format('YYYY-MM-DD HH:mm:ss:SSS'),
      sortComparator: (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime(),
      flex: 1
    },
    { field: 'severity', headerName: 'Severity' },
    {
      field: 'task',
      headerName: 'Task',
      sortComparator: (v1, v2) => v1.localeCompare(v2, undefined, { numeric: true }),
      align: 'right'
    },
    {
      field: 'step',
      headerName: 'Step',
      sortComparator: (v1, v2) => v1.localeCompare(v2, undefined, { numeric: true }),
      align: 'right'
    },
    { field: 'blockid', headerName: 'Block Id', sortComparator: (v1, v2) => Number(v1) - Number(v2), align: 'right' },
    { field: 'blockname', headerName: 'Block Name', flex: 2 },
    { field: 'message', headerName: 'Message', flex: 5 }
  ];
};

export const callListColumns = (openLog: (page: AnalyticsPage, uuid: string) => void): GridColDef[] => {
  return [
    {
      field: 'OpenLog',
      headerName: '',
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title='Session Flow' placement='top' arrow>
            <IconButton size='small' onClick={() => openLog('Session Flow', row.uuid)}>
              <WavesIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='App Log' placement='top' arrow>
            <IconButton size='small' onClick={() => openLog('App Log', row.uuid)}>
              <ListAltIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Database Log' placement='top' arrow>
            <IconButton size='small' onClick={() => openLog('Database Log', row.uuid)}>
              <StorageIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Prompts Log' placement='top' arrow>
            <IconButton size='small' onClick={() => openLog('Prompts Log', row.uuid)}>
              <VolumeUpIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      width: 140,
      sortable: false
    },
    { field: 'start', headerName: 'Start', flex: 2 },
    { field: 'hostname', headerName: 'Host', flex: 1 },
    { field: 'company', headerName: 'Company', flex: 1 },
    { field: 'appname', headerName: 'Appname', flex: 1 },
    { field: 'duration', headerName: 'Duration (s)', align: 'right' },
    { field: 'uuid', headerName: 'UUID', flex: 1 },
    {
      field: 'channels',
      headerName: 'Channels',
      align: 'right',
      valueGetter: (value: Channel[]) => (value ? value.length : 0)
    }
  ];
};

export const channelColumns = (): GridColDef[] => {
  return [
    {
      field: 'timestamp',
      headerName: 'Start',
      valueGetter: (value) => dayjs(value).format('YYYY-MM-DD HH:mm:ss'),
      flex: 2
    },
    {
      field: 'end',
      headerName: 'End',
      flex: 2,
      valueGetter: (_, row: Channel) => {
        if (!row || !row.timestamp) return;

        const start = dayjs(row.timestamp);
        const duration = Number(row.duration) || 0;
        return start.add(duration, 'seconds').format('YYYY-MM-DD HH:mm:ss');
      }
    },
    { field: 'direction', headerName: 'Direction', flex: 1 },
    { field: 'ani', headerName: 'ANI', flex: 2 },
    { field: 'dnis', headerName: 'DNIS', flex: 2 },
    { field: 'duration', headerName: 'Duration (s)', flex: 1 },
    { field: 'sipcdrid', headerName: 'SipcdrID', flex: 2 },
    { field: 'dispatchercdrid', headerName: 'DispatchercdrID', flex: 2 },
    { field: 'callid', headerName: 'CallID', flex: 4 }
  ];
};
